import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

// ============ SOFTWARE ============

/**
 * Get all software products
 */
export async function getAllSoftware() {
  const snapshot = await getDocs(collection(db, 'software'));
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

const PAGE_SIZE = 20;

/**
 * Get software by category (paginated)
 * @param {string} categoryId
 * @param {import('firebase/firestore').DocumentSnapshot|null} lastDoc - cursor for next page
 */
export async function getSoftwareByCategory(categoryId, lastDoc = null) {
  let q = query(
    collection(db, 'software'),
    where('category', '==', categoryId),
    orderBy('name'),
    limit(PAGE_SIZE)
  );
  if (lastDoc) {
    q = query(
      collection(db, 'software'),
      where('category', '==', categoryId),
      orderBy('name'),
      startAfter(lastDoc),
      limit(PAGE_SIZE)
    );
  }
  const snapshot = await getDocs(q);
  const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
  const hasMore = snapshot.docs.length === PAGE_SIZE;
  return { items, lastVisible, hasMore };
}

/**
 * Get a single software product by slug
 */
export async function getSoftwareBySlug(slug) {
  const q = query(
    collection(db, 'software'),
    where('slug', '==', slug),
    limit(1)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() };
}

/**
 * Get a single software product by ID
 */
export async function getSoftwareById(id) {
  const docRef = doc(db, 'software', id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() };
}

/**
 * Search software by name using Firestore prefix matching.
 * Capitalizes the first letter for case-insensitive prefix match against stored names.
 */
export async function searchSoftware(searchTerm) {
  if (!searchTerm || !searchTerm.trim()) return [];

  // Capitalize first letter to match stored software names
  const term = searchTerm.trim();
  const start = term.charAt(0).toUpperCase() + term.slice(1);
  const end = start + '\uf8ff';

  const q = query(
    collection(db, 'software'),
    where('name', '>=', start),
    where('name', '<=', end),
    orderBy('name'),
    limit(30)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// ============ QUOTES ============

/**
 * Submit a new quote (pending review)
 */
export async function submitQuote(userId, softwareId, quoteData) {
  const quote = {
    softwareId,
    submittedBy: userId,
    submittedAt: serverTimestamp(),
    status: 'pending', // pending | approved | rejected
    data: {
      totalPrice: quoteData.totalPrice,
      seats: quoteData.seats,
      term: quoteData.term, // monthly | annual | multi-year
      tier: quoteData.tier, // e.g., "professional", "enterprise"
      quoteDate: quoteData.quoteDate,
      notes: quoteData.notes || '',
    }
  };
  const docRef = await addDoc(collection(db, 'quotes'), quote);
  return docRef.id;
}

/**
 * Get approved quotes for a software product (anonymized)
 */
export async function getApprovedQuotes(softwareId) {
  const q = query(
    collection(db, 'quotes'),
    where('softwareId', '==', softwareId),
    where('status', '==', 'approved'),
    orderBy('submittedAt', 'desc')
  );
  const snapshot = await getDocs(q);
  // Return anonymized data (no submittedBy)
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      data: data.data,
      submittedAt: data.submittedAt
    };
  });
}

/**
 * Get quotes submitted by a user
 */
export async function getUserQuotes(userId) {
  const q = query(
    collection(db, 'quotes'),
    where('submittedBy', '==', userId),
    orderBy('submittedAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// ============ CATEGORIES ============

/**
 * Get all categories
 */
export async function getCategories() {
  const snapshot = await getDocs(collection(db, 'categories'));
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// ============ ADMIN (for later) ============

/**
 * Get audit log entries (admin only) — last 50 actions
 */
export async function getAuditLog() {
  const q = query(
    collection(db, 'auditLog'),
    orderBy('timestamp', 'desc'),
    limit(50)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      ...data,
      timestamp: data.timestamp?.toDate?.() || null
    };
  });
}

/**
 * Approve or reject a quote (admin only)
 */
export async function updateQuoteStatus(quoteId, status) {
  const docRef = doc(db, 'quotes', quoteId);
  await updateDoc(docRef, {
    status,
    reviewedAt: serverTimestamp()
  });
}
